import { Auth0Client, type RedirectLoginOptions, type User } from '@auth0/auth0-spa-js';
import type { IkeActionDevLocalTokenResponse } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import type { IkeClientService } from '.';
import { NotificationService } from '../components/notification.toast/notification.service';
import { T } from '../i18n';
import { Services } from './index';

export class SSOService implements IkeClientService {
	__priority = 24;

	client: Auth0Client;

	@observable inFlight: boolean = true;
	@observable user: User | null = null;
	@observable token: string | null = null;

	constructor() {
		makeObservable(this);
	}

	@action setUser(user: User) {
		this.user = user;
	}

	@action setToken(token: string) {
		this.token = token;
	}

	@computed get isAuthenticated() {
		return Boolean(this.user);
	}

	async init() {
		// OFFLINE local dev: the server injects auth0.enabled=false and serves a local dev token
		if (Services.Config.config.get('auth0.enabled') === false) {
			await this.initLocalDevSession();
			return;
		}

		const clientId = Services.Config.config.get('auth0.client.id');
		const domain = Services.Config.config.get('auth0.domain');

		if (clientId && domain) {
			this.client = new Auth0Client({
				client_id: clientId,
				domain,
				redirect_uri: window.location.origin,
				audience: `${window.location.origin}/v1/`,
			});

			try {
				//This is ugly but enables us to get the token in Firefox.
				// const token = await this.client.getTokenWithPopup();
				const token = await this.client.getTokenSilently();
				const user = await this.client.getUser();

				Services.Log.Logger.info('Signed in with Auth0', token);

				this.setToken(token);
				this.setUser(user);
				this.inFlight = false;
			} catch (error) {
				const message = error instanceof Error ? error.message : undefined;
				if (message !== 'Login required') {
					Services.Log.Logger.error({ error }, 'Auth0 error');
					if (message) {
						NotificationService.errorAsString(message);
					} else {
						NotificationService.error({ key: T.ERROR_GENERAL });
					}
				}

				this.inFlight = false;
			}
		} else {
			Services.Log.Logger.warn('Unable to start SSO Service: missing client ID or domain variable');
			NotificationService.warn({ key: T.UNABLE_TO_START_SSO_SERVICE });
			this.inFlight = false;
		}
	}

	private async initLocalDevSession() {
		try {
			// Tests can pin a persona via localStorage so the offline login resolves a specific role.
			const persona = window.localStorage.getItem('ike.offline.persona') ?? undefined;
			const response = await fetch('/v1/action/dev-local-token', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify(persona ? { persona } : {}),
			});

			if (!response.ok) {
				throw new Error(`Dev token request failed with status ${response.status}`);
			}

			const body: IkeActionDevLocalTokenResponse = await response.json();

			Services.Log.Logger.warn('Signed in with local dev token', body.user.email);

			this.setToken(body.token);
			this.setUser(body.user);
		} catch (error) {
			Services.Log.Logger.error({ error }, 'Local dev login failed');
			NotificationService.errorAsString('Local dev login failed — is the server running with OFFLINE=yes?');
		} finally {
			this.inFlight = false;
		}
	}

	/**
	 * Trigger a login attempt
	 * @param options Extra preferences for the login function using {@link RedirectLoginOptions}
	 */
	async login(options: RedirectLoginOptions = {}) {
		if (this.client) {
			await this.client.loginWithRedirect(options);
		}
	}

	/**
	 * Logs the user out
	 * @param federated Logs the user out of their IDP so they will have to full log back in again to access
	 * @refer https://auth0.com/docs/authenticate/login/logout/log-users-out-of-idps
	 */
	async logout(federated: boolean = false) {
		if (this.client) {
			await this.client.logout({ federated });
		} else if (Services.Config.config.get('auth0.enabled') === false) {
			// Offline dev session: a reload fetches a fresh dev token (also recovers expired sessions)
			window.location.reload();
		}
	}

	async resetPassword() {
		await Services.Rest.rest.SSOUser.triggerResetPassword({ email: this.user.email });
	}
}
